import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { sourcesKeys } from "@/hooks/useSources";
import { patchSource } from "@/services/sourcesApi";
import {
  buildScraperConfigPatch,
  effectiveThrottleAuto,
  isEmptyPatch,
  isThrottleAtFloor,
  scraperConfigToForm,
  SCRAPER_FIELD_CONSTRAINTS,
  type ScraperConfigForm,
  type ScraperNumericField,
  type SourceScraperConfig,
} from "@/utils/scraperThrottle";
import { toast } from "sonner";

const NUMERIC_FIELD_ORDER: ScraperNumericField[] = [
  "scraper_batch_size",
  "scraper_batch_delay",
  "scraper_request_delay",
  "scraper_max_retries",
  "scrape_interval_hours",
];

function emptyForm(): ScraperConfigForm {
  return scraperConfigToForm({
    scraper_batch_size: null,
    scraper_batch_delay: null,
    scraper_request_delay: null,
    scraper_max_retries: null,
    scrape_interval_hours: null,
    scraper_throttle_auto: true,
    scraper_clean_run_streak: 0,
  });
}

export function ThrottleConfigDialog({
  open,
  onOpenChange,
  sourceId,
  sourceName,
  config,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceId: string | null;
  sourceName: string;
  config: SourceScraperConfig | undefined;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<ScraperConfigForm>(emptyForm);
  const [autoTouched, setAutoTouched] = useState(false);
  const [pending, setPending] = useState(false);
  const appliedSourceId = useRef<string | null>(null);

  useEffect(() => {
    if (!open) {
      appliedSourceId.current = null;
      return;
    }
    if (!config || !sourceId) return;
    if (appliedSourceId.current === sourceId) return;
    setForm(scraperConfigToForm(config));
    setAutoTouched(false);
    appliedSourceId.current = sourceId;
  }, [open, config, sourceId]);

  const preview = useMemo(() => {
    if (!config) return null;
    return buildScraperConfigPatch(config, form, autoTouched);
  }, [autoTouched, config, form]);

  const fieldErrors = preview && !preview.ok ? preview.errors : {};
  const patch = preview && preview.ok ? preview.patch : null;
  const throttleFieldsChanged =
    preview && preview.ok ? preview.throttleFieldsChanged : false;
  const displayedAuto = config
    ? effectiveThrottleAuto({
        originalAuto: config.scraper_throttle_auto,
        formAuto: form.scraper_throttle_auto,
        autoTouched,
        throttleFieldsChanged: throttleFieldsChanged ?? false,
      })
    : form.scraper_throttle_auto;

  const canSave =
    Boolean(sourceId && config && patch && !isEmptyPatch(patch)) &&
    !pending &&
    preview?.ok === true;

  const handleFieldChange = (field: ScraperNumericField, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!sourceId || !config || !patch || isEmptyPatch(patch)) return;
    setPending(true);
    try {
      await patchSource(sourceId, patch);
      await queryClient.invalidateQueries({ queryKey: sourcesKeys.all });
      toast.success(`Updated throttle for ${sourceName}`);
      onOpenChange(false);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to update throttle",
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Throttle and cadence</DialogTitle>
          <DialogDescription>
            Source {sourceId ?? "—"}. Empty fields restore the global default.
            Editing a throttle value pins auto-tuning off so that number is
            kept. Cadence does not pin.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          {NUMERIC_FIELD_ORDER.map((field) => {
            const constraint = SCRAPER_FIELD_CONSTRAINTS[field];
            const fullWidth = field === "scrape_interval_hours";
            return (
              <div
                key={field}
                className={fullWidth ? "space-y-2 sm:col-span-2" : "space-y-2"}
              >
                <Label htmlFor={field}>{constraint.label}</Label>
                <Input
                  id={field}
                  inputMode={constraint.integer ? "numeric" : "decimal"}
                  value={form[field]}
                  onChange={(event) =>
                    handleFieldChange(field, event.target.value)
                  }
                  placeholder={constraint.placeholder}
                  disabled={pending || !config}
                  aria-invalid={Boolean(fieldErrors[field])}
                />
                <p className="text-xs text-muted-foreground">
                  {fieldErrors[field] ?? constraint.hint}
                </p>
              </div>
            );
          })}
        </div>
        <div className="flex items-start justify-between gap-4 rounded-md border p-3">
          <div className="space-y-1">
            <Label htmlFor="scraper_throttle_auto">Auto-tune throttle</Label>
            <p className="text-xs text-muted-foreground">
              When on, the scraper may tighten these values after 429s and relax
              one notch after 3 clean full scrapes. Turn back on after an edit
              to re-arm.
            </p>
          </div>
          <Switch
            id="scraper_throttle_auto"
            checked={displayedAuto}
            onCheckedChange={(checked) => {
              setAutoTouched(true);
              setForm((prev) => ({ ...prev, scraper_throttle_auto: checked }));
            }}
            disabled={pending || !config}
            aria-label="Auto-tune throttle"
          />
        </div>
        <p className="text-sm text-muted-foreground">
          Clean run streak:{" "}
          <span className="font-medium text-foreground">
            {config?.scraper_clean_run_streak ?? 0}
          </span>
          <span className="text-xs"> (read-only)</span>
        </p>
        {throttleFieldsChanged && !displayedAuto ? (
          <p className="text-sm text-amber-600 dark:text-amber-400">
            Saving will pin auto-tuning off so these values are not overwritten.
          </p>
        ) : null}
        {throttleFieldsChanged && displayedAuto ? (
          <p className="text-sm text-muted-foreground">
            Auto-tune stays on — a later rate-limited run may overwrite these
            values.
          </p>
        ) : null}
        {config && isThrottleAtFloor(config.scraper_batch_size) ? (
          <p className="text-sm text-amber-600 dark:text-amber-400">
            Batch size is 1, the floor. The daily health sweep flags this source
            if it is still being rate-limited.
          </p>
        ) : null}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!canSave}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
